import {useCallback} from 'react';
import {useSelector} from 'react-redux';
import {useAppDispatch, type RootState} from '../../../store/store';
import {selectTenant, selectUserId} from '../../../store/features/Auth/authSlice';
import {setCallFrom} from '../../../store/features/calls/dialerSlice';
import {useLazyGetStatusesQuery} from '../../../store/features/Team/teamService';
import type {UserElement} from '../../../store/features/Users/userService';
import {useCallService} from '../../../services/callService';
import {blockCallDuringGroupCall} from '../../../services/groupCallBusyGuard';
import {promptDndCall} from '../store/callSurfaceSlice';
import {startInternalCall} from '../matrix/matrixActions';

export interface DialerActions {
    readonly callNumber: (e164: string) => void;
    readonly callTeamMember: (user: UserElement) => void;
}

export const useDialerActions = (): DialerActions => {
    const dispatch = useAppDispatch();
    const {makeCall} = useCallService();
    const callState = useSelector((state: RootState) => state.dialer.callState);
    const currentUserId = useSelector(selectUserId);
    const tenant = useSelector(selectTenant);
    const [fetchStatuses] = useLazyGetStatusesQuery();

    const callNumber = useCallback((e164: string) => {
        if (blockCallDuringGroupCall(callState, dispatch) || callState !== 'IDLE') {
            return;
        }
        void makeCall({msisdn: e164, callTo: e164, isInternal: false});
    }, [callState, dispatch, makeCall]);

    const callTeamMember = useCallback((user: UserElement) => {
        const callee = {userId: user.userId ?? user.id, firstName: user.firstName, lastName: user.lastName};
        void (async () => {
            const statuses = await fetchStatuses({users: [callee.userId]}).unwrap().catch(() => undefined);
            if (statuses?.[0]?.doNotDisturbUserStatus?.enabled) {
                dispatch(setCallFrom(callee));
                dispatch(promptDndCall());
                return;
            }
            await startInternalCall(callee, {callState, currentUserId: String(currentUserId ?? ''), tenant: String(tenant ?? ''), dispatch});
        })();
    }, [callState, currentUserId, dispatch, fetchStatuses, tenant]);

    return {callNumber, callTeamMember};
};
